// directive for controlling the score of a broadcasted sporting event
//
//    <scoreboard-controls broadcast="vm.broadcast"
//                         scoreboard="vm.scoreboard" />
//
//
// depending on the sport inside the scoreboard configuration object, different
// controls will be presented.
//
// upon changing the score, the change will be persisted back to the server.

let SCOREBOARD_CHANGE_DEBOUNCE_MS = 500; //batch subsequent changes

export default function scoreboardControls(ApiConfig, Broadcast, $interval) {
  'ngInject';

  return {
    template: require('./scoreboard-controls.html'),
    scope: {
      broadcast: '=',
      scoreboard: '=',
    },
    link: (scope, element) => {
      ApiConfig.then(() => (scope.sports = ApiConfig.stream.sports));

      let config = configForSport(scope.scoreboard.sport);
      scope.controls = config.controls;
      scope.increments = config.increments;
      scope.presets = config.presets;

      function reloadScore() {
        if (!scope.broadcast.id) {
          console.warn('Broadcast is not defined; cannot load score', scope.broadcast);
          return;
        }
        return Broadcast.getScore(
          { id: scope.broadcast.id },
          (score) => (scope.score = score || {})
        ).$promise;
      }

      scope.update = function (key) {
        console.log('Update fired for', key, 'to', scope.score[key]);
        saveScoreDebounced();
      };

      scope.handlePresetClick = function (preset) {
        console.log('Preset clicked', preset);
        if (preset.confirm) {
          if (!confirm(preset.confirm)) {
            return;
          }
        }
        scope.score = preset.set(scope.score);
        scope.update();
      };

      scope.handleIncrementClick = function (field, increment) {
        console.log('Increment clicked', field, increment);
        scope.score[field] = scope.score[field] + increment;
        scope.update();
      };

      function saveScore() {
        // TODO: der grabbing server score first, then merging changes if
        // necessary (or warning user that someone else might be editing). this
        // could be easier to manage oddities than trying to refresh from the
        // server except while in a control editing.
        if (!scope.broadcast.id) {
          console.warn('Cannot save score; broadcast is not defined');
          return;
        }
        Broadcast.saveScore({ id: scope.broadcast.id }, scope.score, (score) =>
          console.log('Saved score successfully!')
        );
      }

      let saveScoreDebounced = _.debounce(saveScore, SCOREBOARD_CHANGE_DEBOUNCE_MS);

      scope.loadingPromise = reloadScore();

      // XXX: should we poll for other browsers making changes to scoreboard or
      // assume this user is source of truth?
      //let SCOREBOARD_REFRESH_INTERVAL_MS = 10000;
      //let reloadHandle = $interval(reloadScore, SCOREBOARD_REFRESH_INTERVAL_MS);
      //scope.$on('$destroy', () => $interval.cancel(reloadHandle));
    },
  };
}

export function defaultScoreForSport(sport) {
  const { controls } = configForSport(sport);
  let data = {};
  controls.forEach((c) => {
    if (c.radioGroup && c.radioGroup.length) {
      data[c.key] = c.radioGroup[0].key;
    } else {
      data[c.key] = c.min;
    }
  });
  return data;
}

export function configForSport(sport) {
  let controls = [];
  let increments = []; // in addition to the standard +/- 1
  let presets = [
    {
      label: 'Reset Scoreboard',
      confirm: 'Are you sure you want to reset the scoreboard?',
      set: (x) => {
        x = _.merge(x, defaultScoreForSport(sport));
        x.home_score = 0;
        x.away_score = 0;
        return x;
      },
    },
  ];

  switch (sport.toLowerCase()) {
    case 'basketball (2 halves)':
      controls = [{ label: 'Half', key: 'period', min: 1, max: 9, className: 'col-xs-12' }];
      increments = [1, 2, 3];
      break;
    case 'basketball (4 quarters)':
      controls = [{ label: 'Quarter', key: 'period', min: 1, max: 9, className: 'col-xs-12' }];
      increments = [1, 2, 3];
      break;
    case 'soccer':
      controls = [{ label: 'Quarter/Half', key: 'period', min: 1, max: 9, className: 'col-xs-12' }];
      break;
    case 'lacrosse':
      controls = [{ label: 'Quarter/Half', key: 'period', min: 1, max: 9, className: 'col-xs-12' }];
      break;
    case 'hockey':
      controls = [{ label: 'Period', key: 'period', min: 1, max: 9, className: 'col-xs-12' }];
      break;
    case 'wrestling':
      controls = [{ label: 'Round', key: 'round', min: 1, max: 9, className: 'col-xs-12' }];
      increments = [1, 2, 3];
      break;
    case 'baseball/softball':
      controls = [
        { label: 'Inning', key: 'inning', min: 1, max: 99, className: 'col-xs-12 col-md-6' },
        {
          label: null,
          key: 'inning_half',
          className: 'col-xs-12 col-md-6',
          radioGroup: [
            { label: 'Top', key: 'top' },
            { label: 'Bottom', key: 'bottom' },
          ],
        },
        { label: 'Outs', key: 'outs', min: 0, max: 3, className: 'col-xs-12 col-md-4' },
        { label: 'Balls', key: 'balls', min: 0, max: 4, className: 'col-xs-12 col-md-4' },
        { label: 'Strikes', key: 'strikes', min: 0, max: 3, className: 'col-xs-12 col-md-4' },
      ];
      increments = [1, 2, 3, 4];
      presets = presets.concat([
        {
          label: 'Next Batter',
          set: (x) => {
            x.balls = 0;
            x.strikes = 0;
            return x;
          },
        },
        {
          label: 'Next Half-Inning',
          set: (x) => {
            if (x.inning_half == 'top') {
              x.inning_half = 'bottom';
            } else {
              x.inning_half = 'top';
              x.inning = (x.inning || 0) + 1;
            }
            x.outs = 0;
            x.balls = 0;
            x.strikes = 0;
            return x;
          },
        },
      ]);
      break;
    case 'football':
      controls = [
        {
          label: 'Quarter',
          key: 'period',
          min: 1,
          max: 9,
          className: 'col-xs-12 col-md-4 col-lg-3',
        },
        { label: 'Down', key: 'down', min: 1, max: 4, className: 'col-xs-6 col-md-4 col-lg-3' },
        {
          label: 'Yards to Go',
          key: 'yards_to_go',
          min: 1,
          max: 100,
          className: 'col-xs-6 col-md-4 col-lg-3',
        },
        {
          label: 'Possession',
          key: 'possession',
          className: 'col-xs-12 col-lg-3 no-padding',
          radioGroup: [
            { label: 'Away', key: 'away' },
            { label: 'Home', key: 'home' },
          ],
        },
      ];
      increments = [1, 2, 3, 6];
      presets = presets.concat([
        {
          label: 'New Set of Downs',
          set: (x) => {
            x.yards_to_go = 10;
            x.down = 1;
            return x;
          },
        },
      ]);
      break;
    case 'volleyball':
      controls = [
        {
          label: 'Service',
          key: 'service',
          className: 'col-xs-12 col-lg-3 no-padding',
          radioGroup: [
            { label: 'Away', key: 'away' },
            { label: 'Home', key: 'home' },
          ],
        },
        { label: 'Set', key: 'set', min: 1, max: 9, className: 'col-xs-12 col-lg-3' },
        {
          label: 'Home Games Won',
          key: 'home_games_won',
          min: 0,
          max: 99,
          className: 'col-xs-6 col-lg-3',
        },
        {
          label: 'Away Games Won',
          key: 'away_games_won',
          min: 0,
          max: 99,
          className: 'col-xs-6 col-lg-3',
        },
      ];
      break;
    default:
      console.warn('Unknown sport', scoreboard);
      break;
  }

  return { controls, increments, presets };
}



// WEBPACK FOOTER //
// ./app/pages/events/scoreboard-controls.js